import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoPeopleOutline,
  IoTicketOutline,
  IoCalendarOutline,
  IoLocationOutline,
  IoCallOutline,
  IoMailOutline,
  IoBusinessOutline,
} from 'react-icons/io5';
import { fetchUsers, fetchTickets, fetchVisits } from '../api/api';
import './Dashboard.css';

const AreaHeadDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Get current user ID from localStorage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = String(storedUser?.id);

      console.log('[AreaHeadDashboard] Current User ID:', currentUserId);

      const [allUsers, ticketsData, visitsData] = await Promise.all([
        fetchUsers(),
        fetchTickets(),
        fetchVisits(),
      ]);

      console.log('[AreaHeadDashboard] All users count:', allUsers.length);
      console.log('[AreaHeadDashboard] Sample user:', allUsers[0]);

      // Filter users assigned to this area head (handle both string/number types)
      const assignedUsers = allUsers.filter((u) => {
        const userAreaHeadId = String(u.areaHeadId || u.area_head_id || '');
        const match = userAreaHeadId === currentUserId;
        if (match) {
          console.log('[AreaHeadDashboard] Matched user:', u.name, 'areaHeadId:', userAreaHeadId);
        }
        return match;
      });

      console.log('[AreaHeadDashboard] Assigned users count:', assignedUsers.length);

      setUsers(assignedUsers);
      setTickets(ticketsData);
      setVisits(visitsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter tickets and visits for assigned users only
  const userIds = users.map((u) => u.id);
  const myTickets = tickets.filter((t) => userIds.includes(t.created_by || t.createdBy));
  const myVisits = visits.filter((v) => userIds.includes(v.user_id || v.userId));

  const openTickets = myTickets.filter((t) => t.status === 'open').length;
  const inProgressTickets = myTickets.filter((t) => t.status === 'in-progress').length;
  const upcomingVisits = myVisits.filter((v) => v.status === 'scheduled').length;

  const statCards = [
    { title: 'My Customers', value: users.length, icon: IoPeopleOutline, color: 'var(--primary)' },
    { title: 'Open Tickets', value: openTickets, icon: IoTicketOutline, color: 'var(--warning)' },
    { title: 'In Progress', value: inProgressTickets, icon: IoTicketOutline, color: 'var(--info)' },
    { title: 'Upcoming Visits', value: upcomingVisits, icon: IoCalendarOutline, color: 'var(--success)' },
  ];

  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  const handleViewUserTickets = (userId) => {
    navigate(`/area-head/tickets?userId=${userId}`);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Area Head Dashboard</h1>
        <p>Manage your assigned customers, tickets, and visits</p>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="stat-icon" style={{ background: `${stat.color}15`, color: stat.color }}>
                <Icon />
              </div>
              <div className="stat-content">
                <h3>{loading ? '—' : stat.value}</h3>
                <p>{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
        {/* Assigned Users List */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>My Assigned Customers</h2>
            <span className="badge">{users.length}</span>
          </div>
          <div className="users-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {loading ? (
              <p>Loading...</p>
            ) : users.length > 0 ? (
              users.map((user) => (
                <div
                  key={user.id}
                  className="user-card"
                  onClick={() => handleUserClick(user)}
                  style={{
                    padding: '16px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    cursor: 'pointer',
                    background: selectedUser?.id === user.id ? 'var(--primary-light)' : 'white',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'var(--primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '16px' }}>{user.name}</h4>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {user.user_id || user.userId}
                      </p>
                    </div>
                    <span
                      className="status-badge"
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: user.is_active ? 'var(--success-light)' : 'var(--danger-light)',
                        color: user.is_active ? 'var(--success)' : 'var(--danger)',
                      }}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {user.phone && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IoCallOutline /> {user.phone}
                      </span>
                    )}
                    {user.email && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IoMailOutline /> {user.email}
                      </span>
                    )}
                    {user.area && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IoLocationOutline /> {user.area}
                      </span>
                    )}
                  </div>
                  {(user.site_name || user.siteName) && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <IoBusinessOutline /> {user.site_name || user.siteName}
                    </div>
                  )}
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewUserTickets(user.id);
                      }}
                    >
                      View Tickets
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/area-head/visits?userId=${user.id}`);
                      }}
                    >
                      View Visits
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No assigned customers found.</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Tickets from My Customers</h2>
          </div>
          <div className="tickets-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {loading ? (
              <p>Loading...</p>
            ) : myTickets.length > 0 ? (
              myTickets
                .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
                .slice(0, 10)
                .map((ticket) => {
                  const customer = users.find((u) => u.id === (ticket.created_by || ticket.createdBy));
                  return (
                    <div
                      key={ticket.id}
                      className="ticket-item"
                      onClick={() => navigate(`/area-head/tickets/${ticket.id}`)}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        background: 'white',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px' }}>{ticket.title}</h4>
                          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                            From: {customer?.name || 'Unknown'}
                          </p>
                        </div>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            textTransform: 'capitalize',
                            background:
                              ticket.status === 'open'
                                ? 'var(--warning-light)'
                                : ticket.status === 'in-progress'
                                ? 'var(--info-light)'
                                : ticket.status === 'resolved'
                                ? 'var(--success-light)'
                                : 'var(--text-light)',
                            color:
                              ticket.status === 'open'
                                ? 'var(--warning)'
                                : ticket.status === 'in-progress'
                                ? 'var(--info)'
                                : ticket.status === 'resolved'
                                ? 'var(--success)'
                                : 'var(--text-secondary)',
                          }}
                        >
                          {ticket.status}
                        </span>
                      </div>
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                        {ticket.description?.substring(0, 80)}...
                      </p>
                    </div>
                  );
                })
            ) : (
              <p>No tickets from your customers.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaHeadDashboard;
